import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Lock, Loader2, Send, Pencil, Trash2, MessagesSquare, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { internalCommentsAPI } from '../lib/api';
import { useAuthStore } from '../store';

const fmt = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d.getTime()) ? d.toLocaleString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
};
const firstName = (n) => (n || '').trim().split(/\s+/)[0] || n;
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Texto con @menciones resaltadas
function RichText({ text, members }) {
  const names = members.map((m) => firstName(m.name)).filter(Boolean).sort((a, b) => b.length - a.length);
  if (!names.length) return <>{text}</>;
  const re = new RegExp(`(@(?:${names.map(escapeRe).join('|')}))`, 'g');
  return <>{text.split(re).map((part, i) => (part.startsWith('@') && names.includes(part.slice(1))
    ? <span key={i} className="font-semibold text-primary bg-primary/10 rounded px-0.5">{part}</span>
    : <React.Fragment key={i}>{part}</React.Fragment>))}</>;
}

// Textarea con autocompletado de @menciones
function MentionInput({ value, onChange, members, onSubmit, testid, autoFocus, placeholder }) {
  const ref = useRef(null);
  const [query, setQuery] = useState(null);
  const [hi, setHi] = useState(0);
  const options = useMemo(() => (query === null ? [] : members.filter((m) => (m.name || '').toLowerCase().includes(query.toLowerCase())).slice(0, 6)), [query, members]);

  const detect = (val, pos) => {
    const m = /(^|\s)@([\p{L}]*)$/u.exec(val.slice(0, pos));
    setQuery(m ? m[2] : null); setHi(0);
  };
  const pick = (member) => {
    const el = ref.current; const pos = el.selectionStart;
    const before = value.slice(0, pos).replace(/@([\p{L}]*)$/u, `@${firstName(member.name)} `);
    const next = before + value.slice(pos);
    onChange(next); setQuery(null);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(before.length, before.length); });
  };
  return (
    <div className="relative">
      <Textarea ref={ref} value={value} rows={3} autoFocus={autoFocus} placeholder={placeholder}
        className="resize-none text-sm" data-testid={testid}
        onChange={(e) => { onChange(e.target.value); detect(e.target.value, e.target.selectionStart); }}
        onKeyDown={(e) => {
          if (options.length) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => (h + 1) % options.length); return; }
            if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => (h - 1 + options.length) % options.length); return; }
            if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(options[hi]); return; }
            if (e.key === 'Escape') { setQuery(null); return; }
          }
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onSubmit?.(); }
        }} />
      {options.length > 0 && (
        <div className="absolute z-50 left-2 bottom-full mb-1 w-56 rounded-md border bg-popover shadow-md py-1" data-testid="mention-options">
          {options.map((m, i) => (
            <button key={m.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(m); }}
              className={`w-full text-left px-3 py-1.5 text-sm ${i === hi ? 'bg-muted' : ''}`} data-testid={`mention-option-${m.id}`}>
              <span className="font-medium">{m.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const mentionIdsIn = (text, members) => members.filter((m) => new RegExp(`@${escapeRe(firstName(m.name))}(?![\\p{L}])`, 'u').test(text)).map((m) => m.id);

export default function InternalCommentsThread({ postId, team = [], onRead }) {
  const { user } = useAuthStore();
  const members = useMemo(() => team.filter((m) => ['ceo', 'manager', 'member'].includes(m.role) && m.is_active !== false), [team]);
  const [comments, setComments] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState('');
  const endRef = useRef(null);
  const myId = user?.id || user?.user_id;
  const isCeo = user?.role === 'ceo';

  useEffect(() => {
    let alive = true;
    setComments(null);
    internalCommentsAPI.list(postId).then((r) => { if (alive) { setComments(r.comments || []); onRead?.(postId); } })
      .catch(() => alive && setComments([]));
    return () => { alive = false; };
  }, [postId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }); }, [comments?.length]);

  const send = async () => {
    const t = text.trim(); if (!t) return;
    setSending(true);
    try {
      const c = await internalCommentsAPI.add(postId, t, mentionIdsIn(t, members));
      setComments((prev) => [...(prev || []), c]); setText('');
      if (c.mentions?.length) toast.success(`Comentario enviado · ${c.mentions.length} ${c.mentions.length === 1 ? 'persona avisada' : 'personas avisadas'}`);
    } catch (e) { toast.error(e?.message || 'No se pudo enviar'); }
    finally { setSending(false); }
  };
  const saveEdit = async () => {
    const t = editText.trim(); if (!t) return;
    try {
      const c = await internalCommentsAPI.edit(editId, t, mentionIdsIn(t, members));
      setComments((prev) => prev.map((x) => (x.id === c.id ? c : x))); setEditId(null);
    } catch (e) { toast.error(e?.message || 'No se pudo editar'); }
  };
  const remove = async (id) => {
    if (!window.confirm('¿Borrar este comentario?')) return;
    try { await internalCommentsAPI.remove(id); setComments((prev) => prev.filter((x) => x.id !== id)); }
    catch (e) { toast.error(e?.message || 'No se pudo borrar'); }
  };

  return (
    <Card className="border-violet-200" data-testid="internal-comments-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <MessagesSquare className="w-4 h-4 text-violet-600" /> Comentarios internos del equipo
        </CardTitle>
        <p className="text-[11px] text-violet-700 flex items-center gap-1" data-testid="internal-comments-private-note">
          <Lock className="w-3 h-3" /> Solo equipo · el cliente nunca ve estos comentarios
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {comments === null ? (
          <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-muted-foreground" /></div>
        ) : comments.length === 0 ? (
          <p className="text-xs text-muted-foreground py-3 text-center border border-dashed rounded-md" data-testid="internal-comments-empty">Sin comentarios internos. Escribe @ para mencionar a alguien.</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="internal-comments-list">
            {comments.map((c) => {
              const mine = c.author_id === myId;
              return (
                <div key={c.id} className={`group text-sm rounded-md px-2.5 py-1.5 border ${mine ? 'bg-violet-50 border-violet-200' : 'bg-muted/40'}`} data-testid={`internal-comment-${c.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className="text-[11px] font-semibold" data-testid={`internal-comment-author-${c.id}`}>{c.author_name}</span>
                    <span className="flex items-center gap-1">
                      <span className="text-[10px] text-muted-foreground">{fmt(c.created_at)}{c.edited_at ? ' · editado' : ''}</span>
                      {mine && editId !== c.id && (
                        <button onClick={() => { setEditId(c.id); setEditText(c.texto); }} className="p-0.5 rounded hover:bg-background opacity-60 hover:opacity-100" title="Editar" data-testid={`internal-comment-edit-${c.id}`}><Pencil className="w-3 h-3" /></button>
                      )}
                      {(mine || isCeo) && editId !== c.id && (
                        <button onClick={() => remove(c.id)} className="p-0.5 rounded hover:bg-background text-red-500 opacity-60 hover:opacity-100" title="Borrar" data-testid={`internal-comment-delete-${c.id}`}><Trash2 className="w-3 h-3" /></button>
                      )}
                    </span>
                  </div>
                  {editId === c.id ? (
                    <div className="space-y-1.5">
                      <MentionInput value={editText} onChange={setEditText} members={members} onSubmit={saveEdit} testid={`internal-comment-edit-input-${c.id}`} autoFocus />
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditId(null)}><X className="w-3.5 h-3.5 mr-1" /> Cancelar</Button>
                        <Button size="sm" className="h-7" onClick={saveEdit} disabled={!editText.trim()} data-testid={`internal-comment-save-${c.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Guardar</Button>
                      </div>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap text-foreground/90"><RichText text={c.texto} members={members} /></p>
                  )}
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
        )}
        <div className="space-y-2 pt-1 border-t">
          <MentionInput value={text} onChange={setText} members={members} onSubmit={send} testid="internal-comment-input"
            placeholder="Escribe un comentario para el equipo… usa @ para mencionar (Ctrl+Enter envía)" />
          <Button size="sm" className="w-full bg-violet-600 hover:bg-violet-700 text-white" onClick={send} disabled={sending || !text.trim()} data-testid="internal-comment-send">
            {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Comentar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Indicador compacto para tarjetas (total + no leídos)
export function CommentsBadge({ info, className = '' }) {
  if (!info?.total) return null;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold rounded-full px-1.5 leading-4 ${info.unread ? 'bg-violet-600 text-white' : 'bg-violet-100 text-violet-700'} ${className}`}
      title={info.unread ? `${info.unread} comentario(s) interno(s) sin leer` : `${info.total} comentario(s) interno(s)`}
      data-testid="internal-comments-badge">
      <MessagesSquare className="w-2.5 h-2.5" />{info.unread || info.total}
    </span>
  );
}
